import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import { useSuppliesConfig } from './use-supplies-config';
import { ProductCard } from '../../../supplies-page/components/product-card/product-card';
import { ShipStationSuppliesLink } from './shipstation-supplies-link';
import type { SuppliesDisplayLocation } from './supplies-page-constants';
import style from './supplies-card-container.module.less';

const CARD_MIN_WIDTH = 190;
const CARD_GAP = 16;

type SuppliesCardContainerProps = {
  page: SuppliesDisplayLocation;
};

export const SuppliesCardContainer = ({ page }: SuppliesCardContainerProps) => {
  const { title, subtextMessage, cards } = useSuppliesConfig(page);
  const containerRef = useRef<HTMLDivElement>(null);
  const [maxCards, setMaxCards] = useState(4);

  // Calculate max visible cards based on container width to prevent overflow
  useEffect(() => {
    const calculateMaxCards = () => {
      if (!containerRef.current) {
        return;
      }

      const containerWidth = containerRef.current.offsetWidth;
      const availableWidth = containerWidth;
      const cardsCount = Math.floor((availableWidth + CARD_GAP) / (CARD_MIN_WIDTH + CARD_GAP));
      const calculated = Math.max(1, Math.min(cardsCount, cards.length));

      setMaxCards(calculated);
    };

    calculateMaxCards();

    const resizeObserver = new ResizeObserver(calculateMaxCards);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [cards.length]);

  if (cards.length === 0) {
    return null;
  }

  const displayedCards = cards.slice(0, maxCards);

  return (
    <FlexColumn className={style['supplies-card-container']}>
      <FlexColumn className={style.header}>
        <Text className={style.title} textType={TextType.H3} value={title} />
        <Text
          className={style.subtext}
          textType={TextType.Paragraph}
          value={<FormattedMessage {...subtextMessage} values={{ link: <ShipStationSuppliesLink source={page} /> }} />}
        />
      </FlexColumn>
      <FlexRow ref={containerRef} className={style['cards-wrapper']}>
        {displayedCards.map((card) => (
          <ProductCard
            key={card.productId}
            compareAtPrice={card.compareAtPrice}
            imageAlt={card.imageAlt}
            imageUrl={card.imageUrl}
            page={page}
            price={card.price}
            productId={card.productId}
            productName={card.itemName}
            variantId={card.variantId}
          />
        ))}
      </FlexRow>
    </FlexColumn>
  );
};
